import React, { useState, useEffect, useRef } from "react";
import { AdminCertificate, CreateCertificateRequest } from "../../types/admin";
import { certificatesApi } from "../../services/api";
import { useToast } from "./Toast";

const emptyForm: CreateCertificateRequest = {
  name: "",
  descriptionPt: "",
  descriptionEn: "",
  issuerName: "",
  issueDate: "",
  expirationDate: null,
  credentialId: "",
  credentialUrl: "",
  displayOrder: null,
};

const formatDateForInput = (iso: string | null): string => {
  if (!iso) return "";
  return iso.substring(0, 10);
};

const isPdfFile = (name: string | null | undefined): boolean => {
  if (!name) return false;
  return name.toLowerCase().endsWith(".pdf");
};

const CertificatesPage: React.FC = () => {
  const { showToast } = useToast();
  const [certificates, setCertificates] = useState<AdminCertificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [editingCert, setEditingCert] = useState<AdminCertificate | null>(null);
  const [form, setForm] = useState<CreateCertificateRequest>({ ...emptyForm });
  const [file, setFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [issuerLogo, setIssuerLogo] = useState<File | null>(null);
  const [issuerLogoPreviewUrl, setIssuerLogoPreviewUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState<AdminCertificate | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const issuerLogoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCertificates = async () => {
    setLoading(true);
    try {
      const response = await certificatesApi.getAll({ pageSize: 100 });
      if (response.success && response.data) {
        setCertificates(response.data);
      } else {
        showToast(response.message || "Failed to load certificates", "error");
      }
    } catch {
      showToast("Failed to load certificates", "error");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setEditingCert(null);
    setForm({ ...emptyForm });
    setFile(null);
    setFilePreviewUrl(null);
    setIssuerLogo(null);
    setIssuerLogoPreviewUrl(null);
    setErrors({});
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (issuerLogoInputRef.current) issuerLogoInputRef.current.value = "";
    setModalOpen(true);
  };

  const openEdit = (cert: AdminCertificate) => {
    setEditingId(cert.id);
    setEditingCert(cert);
    setForm({
      name: cert.name,
      descriptionPt: cert.descriptionPt,
      descriptionEn: cert.descriptionEn,
      issuerName: cert.issuerName,
      issueDate: formatDateForInput(cert.issueDate),
      expirationDate: formatDateForInput(cert.expirationDate),
      credentialId: cert.credentialId,
      credentialUrl: cert.credentialUrl,
      displayOrder: cert.displayOrder,
    });
    setFile(null);
    setFilePreviewUrl(null);
    setIssuerLogo(null);
    setIssuerLogoPreviewUrl(null);
    setErrors({});
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (issuerLogoInputRef.current) issuerLogoInputRef.current.value = "";
    setModalOpen(true);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.issuerName.trim()) newErrors.issuerName = "Issuer name is required";
    if (!form.issueDate) newErrors.issueDate = "Issue date is required";
    if (!form.descriptionPt.trim()) newErrors.descriptionPt = "Description (PT) is required";
    if (!form.descriptionEn.trim()) newErrors.descriptionEn = "Description (EN) is required";
    if (!editingId && !file) newErrors.file = "Certificate file is required";
    if (form.displayOrder != null) {
      const conflict = certificates.find(
        (c) => c.displayOrder === form.displayOrder && c.id !== editingId
      );
      if (conflict) {
        newErrors.displayOrder = `Order ${form.displayOrder} is already used by "${conflict.name}"`;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        ...form,
        issueDate: form.issueDate ? new Date(form.issueDate).toISOString() : "",
        expirationDate: form.expirationDate ? new Date(form.expirationDate).toISOString() : null,
      };

      if (editingId) {
        const response = await certificatesApi.update(editingId, payload, file || undefined, issuerLogo || undefined);
        if (response.success) {
          showToast(response.message || "Certificate updated successfully");
          setModalOpen(false);
          loadCertificates();
        } else {
          showToast(response.message || "Failed to update", "error");
        }
      } else {
        const response = await certificatesApi.create(payload, file || undefined, issuerLogo || undefined);
        if (response.success) {
          showToast(response.message || "Certificate created successfully");
          setModalOpen(false);
          loadCertificates();
        } else {
          showToast(response.message || "Failed to create", "error");
        }
      }
    } catch {
      showToast("An error occurred", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      const response = await certificatesApi.delete(confirmDelete.id);
      if (response.success) {
        showToast(response.message || "Certificate deleted");
        setConfirmDelete(null);
        loadCertificates();
      } else {
        showToast(response.message || "Failed to delete", "error");
      }
    } catch {
      showToast("Failed to delete", "error");
    }
  };

  const handleDownload = (cert: AdminCertificate) => {
    const url = certificatesApi.getDownloadUrl(cert.id);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Certificates</h1>
        <button className="admin-btn admin-btn-primary" onClick={openCreate}>
          <i className="fa-solid fa-plus" /> Add Certificate
        </button>
      </div>

      {loading ? (
        <div className="admin-loading">
          <div className="loading-spinner" />
        </div>
      ) : certificates.length === 0 ? (
        <div className="admin-empty">
          <i className="fa-solid fa-certificate" />
          <p>No certificates yet</p>
        </div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Issuer</th>
                <th>Issue Date</th>
                <th>Expiration</th>
                <th>Credential ID</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {certificates.map((cert) => (
                <tr key={cert.id}>
                  <td data-label="#">{cert.displayOrder ?? "—"}</td>
                  <td data-label="Name">{cert.name}</td>
                  <td data-label="Issuer">{cert.issuerName}</td>
                  <td data-label="Issue Date">
                    {cert.issueDate ? new Date(cert.issueDate).toLocaleDateString() : "—"}
                  </td>
                  <td data-label="Expiration">
                    {cert.expirationDate
                      ? new Date(cert.expirationDate).toLocaleDateString()
                      : "No expiration"}
                  </td>
                  <td data-label="Credential ID">{cert.credentialId && cert.credentialId !== "0" ? cert.credentialId : "—"}</td>
                  <td data-label="Actions">
                    <div className="admin-table-actions">
                      {cert.fileUrl && (
                        <button
                          className="admin-btn admin-btn-sm admin-btn-secondary"
                          onClick={() => handleDownload(cert)}
                          title="Download"
                        >
                          <i className="fa-solid fa-download" />
                        </button>
                      )}
                      {cert.credentialUrl && (
                        <a
                          href={cert.credentialUrl.startsWith("http") ? cert.credentialUrl : `https://${cert.credentialUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="admin-btn admin-btn-sm admin-btn-secondary"
                          title="View Credential"
                        >
                          <i className="fa-solid fa-up-right-from-square" />
                        </a>
                      )}
                      <button
                        className="admin-btn admin-btn-sm admin-btn-secondary"
                        onClick={() => openEdit(cert)}
                        title="Edit"
                      >
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button
                        className="admin-btn admin-btn-sm admin-btn-danger"
                        onClick={() => setConfirmDelete(cert)}
                        title="Delete"
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      {modalOpen && (
        <div className="admin-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingId ? "Edit Certificate" : "New Certificate"}</h2>
              <button className="admin-modal-close" onClick={() => setModalOpen(false)}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className="admin-modal-body">
              {/* Name */}
              <div className="admin-form-group">
                <label className="admin-form-label">Name *</label>
                <input
                  type="text"
                  className={`admin-form-input${errors.name ? " admin-input-error" : ""}`}
                  value={form.name}
                  onChange={(e) => {
                    setForm({ ...form, name: e.target.value });
                    setErrors((prev) => ({ ...prev, name: "" }));
                  }}
                  placeholder="e.g. AWS Solutions Architect"
                />
                {errors.name && <span className="admin-field-error">{errors.name}</span>}
              </div>

              {/* Issuer Name */}
              <div className="admin-form-group">
                <label className="admin-form-label">Issuer *</label>
                <input
                  type="text"
                  className={`admin-form-input${errors.issuerName ? " admin-input-error" : ""}`}
                  value={form.issuerName}
                  onChange={(e) => {
                    setForm({ ...form, issuerName: e.target.value });
                    setErrors((prev) => ({ ...prev, issuerName: "" }));
                  }}
                  placeholder="e.g. Amazon Web Services"
                />
                {errors.issuerName && <span className="admin-field-error">{errors.issuerName}</span>}
              </div>

              {/* Dates row */}
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label className="admin-form-label">Issue Date *</label>
                  <input
                    type="date"
                    className={`admin-form-input${errors.issueDate ? " admin-input-error" : ""}`}
                    value={form.issueDate}
                    onChange={(e) => {
                      setForm({ ...form, issueDate: e.target.value });
                      setErrors((prev) => ({ ...prev, issueDate: "" }));
                    }}
                  />
                  {errors.issueDate && <span className="admin-field-error">{errors.issueDate}</span>}
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">Expiration Date</label>
                  <input
                    type="date"
                    className="admin-form-input"
                    value={form.expirationDate || ""}
                    onChange={(e) =>
                      setForm({ ...form, expirationDate: e.target.value || null })
                    }
                  />
                </div>
              </div>

              {/* Credential ID */}
              <div className="admin-form-group">
                <label className="admin-form-label">Credential ID</label>
                <input
                  type="text"
                  className="admin-form-input"
                  value={form.credentialId}
                  onChange={(e) => setForm({ ...form, credentialId: e.target.value })}
                  placeholder="e.g. ABC123XYZ"
                />
              </div>

              {/* Credential URL */}
              <div className="admin-form-group">
                <label className="admin-form-label">Credential URL</label>
                <input
                  type="text"
                  className="admin-form-input"
                  value={form.credentialUrl}
                  onChange={(e) => setForm({ ...form, credentialUrl: e.target.value })}
                  placeholder="https://www.credly.com/badges/..."
                />
              </div>

              {/* Display Order */}
              <div className="admin-form-group">
                <label className="admin-form-label">Display Order</label>
                <input
                  type="number"
                  min="1"
                  className={`admin-form-input${errors.displayOrder ? " admin-input-error" : ""}`}
                  value={form.displayOrder ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm({ ...form, displayOrder: val ? parseInt(val, 10) : null });
                    setErrors((prev) => ({ ...prev, displayOrder: "" }));
                  }}
                  placeholder="e.g. 1, 2, 3... (leave empty for default)"
                />
                <span className="admin-form-hint">Lower numbers appear first. Leave empty for default order.</span>
                {errors.displayOrder && <span className="admin-field-error">{errors.displayOrder}</span>}
              </div>

              {/* Description PT */}
              <div className="admin-form-group">
                <label className="admin-form-label">Description (PT) *</label>
                <textarea
                  className={`admin-form-input admin-form-textarea${errors.descriptionPt ? " admin-input-error" : ""}`}
                  value={form.descriptionPt}
                  onChange={(e) => {
                    setForm({ ...form, descriptionPt: e.target.value });
                    setErrors((prev) => ({ ...prev, descriptionPt: "" }));
                  }}
                  rows={3}
                  placeholder="Descrição do certificado em português"
                />
                {errors.descriptionPt && <span className="admin-field-error">{errors.descriptionPt}</span>}
              </div>

              {/* Description EN */}
              <div className="admin-form-group">
                <label className="admin-form-label">Description (EN) *</label>
                <textarea
                  className={`admin-form-input admin-form-textarea${errors.descriptionEn ? " admin-input-error" : ""}`}
                  value={form.descriptionEn}
                  onChange={(e) => {
                    setForm({ ...form, descriptionEn: e.target.value });
                    setErrors((prev) => ({ ...prev, descriptionEn: "" }));
                  }}
                  rows={3}
                  placeholder="Certificate description in English"
                />
                {errors.descriptionEn && <span className="admin-field-error">{errors.descriptionEn}</span>}
              </div>

              {/* Issuer Logo upload */}
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Issuer Logo {editingId ? "(leave empty to keep current)" : ""}
                </label>
                <input
                  ref={issuerLogoInputRef}
                  type="file"
                  className="admin-form-input"
                  accept=".jpg,.jpeg,.png,.webp,.svg"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setIssuerLogo(f);
                    if (f) {
                      setIssuerLogoPreviewUrl(URL.createObjectURL(f));
                    } else {
                      setIssuerLogoPreviewUrl(null);
                    }
                  }}
                />
                <span className="admin-form-hint">JPG, PNG, WebP or SVG — square recommended</span>
              </div>

              {/* Issuer Logo Preview */}
              {(() => {
                const logoUrl = issuerLogoPreviewUrl || (editingCert?.issuerLogoUrl ? certificatesApi.getIssuerLogoUrl(editingCert.id) : null);
                if (!logoUrl) return null;
                return (
                  <div className="admin-form-group">
                    <label className="admin-form-label">Logo Preview</label>
                    <div className="admin-cert-preview">
                      <img
                        src={logoUrl}
                        alt="Issuer logo preview"
                        style={{ maxWidth: 80, maxHeight: 80, objectFit: "contain", borderRadius: "0.5rem", background: "#fff", padding: "0.3rem" }}
                      />
                    </div>
                  </div>
                );
              })()}

              {/* File upload */}
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Certificate File {editingId ? "(leave empty to keep current)" : "*"}
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  className={`admin-form-input${errors.file ? " admin-input-error" : ""}`}
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setFile(f);
                    if (f) {
                      setFilePreviewUrl(URL.createObjectURL(f));
                    } else {
                      setFilePreviewUrl(null);
                    }
                    setErrors((prev) => ({ ...prev, file: "" }));
                  }}
                />
                <span className="admin-form-hint">PDF, JPG, PNG or WebP</span>
                {errors.file && <span className="admin-field-error">{errors.file}</span>}
              </div>

              {/* File preview */}
              {(() => {
                const previewUrl = filePreviewUrl || (editingCert?.fileUrl ? certificatesApi.getDownloadUrl(editingCert.id) : null);
                const previewIsPdf = filePreviewUrl
                  ? (file ? isPdfFile(file.name) : false)
                  : isPdfFile(editingCert?.fileUrl);

                if (!previewUrl) return null;

                return (
                  <div className="admin-form-group">
                    <label className="admin-form-label">Preview</label>
                    <div className="admin-cert-preview">
                      {previewIsPdf ? (
                        <object
                          data={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                          type="application/pdf"
                          className="admin-cert-preview-pdf"
                        >
                          <div className="admin-cert-preview-fallback">
                            <i className="fa-solid fa-file-pdf" />
                            <span>PDF Preview</span>
                          </div>
                        </object>
                      ) : (
                        <img
                          src={previewUrl}
                          alt="Certificate preview"
                          className="admin-cert-preview-img"
                        />
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="admin-modal-footer">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => setModalOpen(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : editingId ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {confirmDelete && (
        <div className="admin-modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="admin-modal admin-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Confirm Delete</h2>
              <button className="admin-modal-close" onClick={() => setConfirmDelete(null)}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="admin-modal-body">
              <p>
                Are you sure you want to delete <strong>{confirmDelete.name}</strong>?
              </p>
            </div>
            <div className="admin-modal-footer">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
              <button className="admin-btn admin-btn-danger" onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificatesPage;
